import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import SearchQuery from "../../components/BuilderComponents/SearchQuery";
import Button from "../../components/FormComponents/Button";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import AddChart from "./AddChart";
import { useAxiosContext } from "../../context/AxiosContext";
import ChartImage from "../../images/chartPreview.png";
import MobileStandard from "../../images/mobile standard.png";
import Morph from "../../images/morph chart.png";
import DarkChart from "../../images/dark chart.png";
import zChart from "../../images/z-new chart.png";
import BlueNew from "../../images/blueNew chart.png";
import Orange from "../../images/orange chart.png";
import Cartoon from "../../images/cartoon chart.png";
import BlueLabel from "../../images/blue label chart.png";
import BWChart from "../../images/bw chart.png";
import AdminChart from "../../images/admin charts.png";
import ProjectChart from "../../images/project chart.png";
import MarketDistributionChart from "../../images/market distribution chart.png";
import DomainChart from "../../images/domain chart.png";
import RiskIssueChart from "../../images/risk issue chart.png";
import BuilderTabs from "../../Layouts/BuilderTabs";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";

const initialConfig = {
  theme: {
    palette: "palette1",
    mode: "light"
  },
  chart: {
    background: "var(--block-bg)",
    foreColor: "#000000",
    dropShadow: {
      enabled: false,
      blur: 5,
      left: 0,
      top: 0,
      color: "#000000",
      opacity: 0.5
    }, // Apply drop shadow settings
    toolbar: { show: false },
    zoom: false
  },
  xaxis: {
    tooltip: {
      enabled: false // Disable tooltip
    },
    type: "category",
    crosshairs: {
      show: false // Disable x-axis crosshair
    },
    position: "bottom",
    offsetY: 0,
    axisBorder: { show: true },
    axisTicks: { show: true },
    title: { style: { fontSize: "12px" } },
    labels: {
      show: true, // Show labels
      style: {},
      rotate: -45, // Rotation angle
      trim: true, // Trim long labels
      offsetX: 0, // Labels left offset
      offsetY: 0 // Labels top offset
    }
  },
  yaxis: {
    show: true,
    crosshairs: {
      show: false // Disable x-axis crosshair
    },
    tickAmount: 5,
    title: { style: { fontSize: "12px" } },
    labels: {
      show: true,
      showDuplicates: false,
      style: {}
    }
  },
  grid: {
    show: true,
    yaxis: {
      lines: { show: true }
    },
    padding: {
      right: 25,
      left: 15
    }
  },
  plotOptions: {
    line: {
      isSlopeChart: false,
      colors: {
        threshold: 0
      }
    },
    bar: {
      borderRadius: 10,
      borderRadiusApplication: "end",
      borderRadiusWhenStacked: "last",
      hideZeroBarsWhenGrouped: false,
      isDumbbell: false,
      isFunnel: false,
      isFunnel3d: true,
      dataLabels: {
        total: {
          enabled: false,
          offsetX: 0,
          offsetY: 0,
          style: {
            color: "#373d3f",
            fontSize: "12px",
            fontWeight: 600
          }
        }
      }
    },
    bubble: {
      zScaling: true
    },
    treemap: {
      borderRadius: 4,
      dataLabels: {
        format: "scale"
      },
      seriesTitle: {
        show: true,
        offsetY: 1,
        offsetX: 1,
        borderColor: "#000",
        borderWidth: 1,
        borderRadius: 2,
        style: {
          background: "rgba(0, 0, 0, 0.6)",
          color: "#fff",
          fontSize: "12px",
          fontWeight: 400,
          cssClass: "",
          padding: {
            left: 6,
            right: 6,
            top: 2,
            bottom: 2
          }
        }
      }
    },
    radialBar: {
      hollow: {
        background: "#fff"
      },
      dataLabels: {
        name: {},
        value: {},
        total: {}
      },
      barLabels: {
        enabled: false,
        offsetX: 0,
        offsetY: 0,
        useSeriesColors: true,
        fontWeight: 600,
        fontSize: "16px"
      }
    },
    pie: {
      donut: {
        labels: {
          name: {},
          value: {},
          total: {}
        }
      }
    }
  },
  fill: { gradient: { colorStops: [], inverseColors: false } },
  stroke: {
    show: true,
    fill: {
      type: "",
      opacity: 0.85,
      gradient: {}
    }
  },
  legend: {
    show: true,
    fontSize: 14,
    offsetY: 0,
    clusterGroupedSeries: true,
    clusterGroupedSeriesOrientation: "vertical",
    markers: {
      size: 7,
      shape: "square"
    },
    itemMargin: {
      vertical: 0
    }
  },
  dataLabels: {
    enabled: false,
    style: {
      fontWeight: 700
    },
    background: {
      dropShadow: {}
    }
  },
  colors: []
};

const Charts = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { axiosInstance, handleError, handleSuccess } = useAxiosContext();

  const [addClick, setAddClick] = useState(false);
  const [charts, setCharts] = useState([]);
  const [filteredCharts, setFilteredCharts] = useState(charts);

  const fetchData = async () => {
    const { data } = await axiosInstance.get("/charts/sysconfigs/type/chart_template");
    setCharts([...data]);
  };

  const handleSave = async (chart) => {
    if (chart.name.length < 3) {
      chart.setErrorCallback("Name Must be at least 3 characters");
      return;
    }
    try {
      const { message } = await axiosInstance.post("/charts/sysconfigs", {
        name: chart.name,
        type: "chart_template",
        config: initialConfig,
        org_id: chart.organization.id,
        org_name: chart.organization.name
      });
      handleSuccess(message);
      fetchData();
    } catch (error) {
      handleError(error);
    } finally {
      setAddClick(false);
    }
  };

  const handleChartDelete = async (e, chartId) => {
    e.stopPropagation();
    try {
      const { message } = await axiosInstance.delete(`/sysconfigs/${chartId}`);
      setCharts((prevCharts) => prevCharts.filter((chart) => chart.id !== chartId));

      handleSuccess(message);
    } catch (error) {
      handleError(error);
    }
  };

  const tabData = [
    {
      label: "Public",
      content: getCharts(filteredCharts, handleChartDelete, navigate, false)
    },
    {
      label: "Org",
      content: getCharts(filteredCharts, handleChartDelete, navigate, true)
    }
  ];

  useEffect(() => {
    fetchData();

    //eslint-disable-next-line
  }, []);

  return (
    <div className="application_container">
      <AppLeftSideBar type={params.orgId ? "app" : "org"} />
      <div className="dashboard_outer_section">
        <div className="dashboard-top-header">
          <h2>Charts</h2>
          <div className="d_flex align_center ml_auto gap_3">
            <SearchQuery data={charts} searchKey="name" onSearch={setFilteredCharts} placeholder="Search Charts" />

            <Button
              type="add"
              variant="primary"
              size="medium"
              onClick={() => setAddClick(true)}
              style={{ height: "36px" }}
            >
              <MaterialIcon name="add" style={{ fontSize: "20px", marginRight: "6px" }} />
              New Chart
            </Button>
          </div>
        </div>
        <div className="org-dashboard-page-body flex_column g_5 overflow_hidden">
          <BuilderTabs tabs={tabData} containerClass="themes_tabs" contentClass="h_100 w_100 overflow_auto" />
        </div>
      </div>

      <BuilderModal isOpen={addClick} onClose={() => setAddClick(false)} modalName="Add Chart">
        <AddChart handleSave={handleSave} />
      </BuilderModal>
    </div>
  );
};

export default Charts;

const getCharts = (filteredCharts, handleChartDelete, navigate, orgCharts) => {
  let charts = filteredCharts;

  if (orgCharts) {
    charts = charts.filter((chart) => (chart.org_id ? true : false));
  } else {
    charts = charts.filter((chart) => (chart.org_id ? false : true));
  }

  return (
    <div className="update_theme_container">
      <div className="theme_iframe_container">
        {charts.map((chart) => {
          return (
            <div className="theme_card" onClick={() => navigate("/chartEditor", { state: chart.id })} key={chart.id}>
              <div className="theme_image">
                <img src={getChartImage(chart.name)} alt={chart.name} className="theme_image" />
              </div>
              <hr className="light-line" />
              <div className="theme_name p_2 flex_row_justify_center_align_center gap_3">
                <label className="theme_label">{`${chart.name} ${chart.org_id && `(${chart.org_name})`}`}</label>
                <MaterialIcon
                  name="delete"
                  className="builder_action_btn"
                  onClick={(e) => handleChartDelete(e, chart.id)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getChartImage = (themeName) => {
  switch (themeName) {
    case "Mobile Standard Theme":
      return MobileStandard;

    case "Morph":
      return Morph;

    case "Dark ":
      return DarkChart;

    case "Z - new":
      return zChart;

    case "Blue new ":
      return BlueNew;

    case "Orange ":
      return Orange;

    case "Cartoon ":
      return Cartoon;

    case "Blue Label ":
      return BlueLabel;

    case "Black and White":
      return BWChart;

    case "Admin Page Charts":
      return AdminChart;

    case "Project Types":
      return ProjectChart;

    case "Market Distribution":
      return MarketDistributionChart;

    case "Domain":
      return DomainChart;

    case "Risk and Issues":
      return RiskIssueChart;

    default:
      return ChartImage;
  }
};
